import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedDropDownRHF,
	EnhancedSnackbar,
	IEnhancedDropDownItem,
	IResponse,
	IServerError,
	ITest,
	SingleColumnLayout,
	StandardPanel,
	TwoColumnLayout,
} from "@websential/cosmic-v20";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, Typography, useTheme } from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";
import { formatDate, savePdfAsFile, scrollPanelFieldsToTop, sortDDLByLabel, useNavigateToLogout } from "../../../common/Common";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { CompanyTD1FormService } from "../../../services/CompanyTD1FormService";
import { EmployeeTD1FormValidationSchema } from "./validator";
import FileSaver from "file-saver";
import { IAppState } from "../../../store/reducers";
import { IDropDownListResponse } from "../../../models/common/IDropDownListResponse";
import { INITIAL_SNACKBAR_DATA } from "../../../common/constants";
import { ITD1Form } from "../../../models/TD1Federal/IFederal";
import { PrintingService } from "../../../services/PrintingService";
import { QuebecFormDataConstant } from "./Panels/QuebecFormData";
import { ResourceDDLDataTableForSlips } from "../../ResourceDDLDataTableForSlips";
import { TD1FormDataConstant } from "./Panels/TD1FormData"
import { TD1FormServices } from "../../../services/TD1FormServices";
import { TD1FormToolbar } from "./TD1FormToolbar";
import { setSelectedDrawerOptionsMenu } from "../../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../../store/actions";
import { yupResolver } from "@hookform/resolvers/yup";

export interface ITD1FormSetup extends ITest { }

const SINGLE_RECORD_DATA_QUERY_KEY = "td1-employee-single-data-record";
const EMPLOYEE_RECORD_DATA_QUERY_KEY = "employee-data-record";
const EMPLOYEE_DDL_BY_STATUS_DATA_QUERY_KEY = "td1-employee-ddl";
const TD1_FORM_BY_EMPLOYEE_ID_DATA_QUERY_KEY = "td1-form-employee-id";
const TD1_FORM_BY_EMPLOYEEID_QUERY_KEY = "td1-form-data-by-employeeId";


const useGetDataTD1Detail = (
	id: string | undefined,
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, id],
		queryFn: () => {
			return CompanyTD1FormService.getDataTD1EmployeeDetailById(id);
		},
		refetchOnWindowFocus: false,
	});

// const useGetTD1EmployeeForm = (
// 	date: string,
// 	provinceId: string,
// 	employeeId: string,
// 	clientId: string,
// ) =>
// 	useQuery({
// 		queryKey: [
// 			EMPLOYEE_RECORD_DATA_QUERY_KEY,
// 			date,
// 			// provinceId,
// 			employeeId,
// 			clientId,
// 		],
// 		queryFn: () => {
// 			return CompanyTD1FormService.getDataEmployeeTD1Form(
// 				date,
// 				provinceId,
// 				employeeId,
// 				clientId,
// 			);
// 		},
// 		refetchOnWindowFocus: false,
// 	});


// const useGetTD1EmployeeDDL = (
// 	clientId: string,
// 	status: number,
// 	taxYear: number,
// ) =>
// 	useQuery({
// 		queryKey: [
// 			EMPLOYEE_DDL_BY_STATUS_DATA_QUERY_KEY,
// 			clientId,
// 			status,
// 			taxYear,
// 		],
// 		queryFn: () => {
// 			return CompanyTD1FormService.getEmployeeDDLByStatus(
// 				clientId,
// 				status,
// 				taxYear
// 			);
// 		},
// 		refetchOnWindowFocus: false,
// 	});
const useGetEmployeeTD1FormDDLByProvinceAndStatus = (
	clientId: string,
	provinceId: string,
	taxYear: number,
	status: number
) =>
	useQuery({
		queryKey: [
			EMPLOYEE_DDL_BY_STATUS_DATA_QUERY_KEY,
			clientId,
			provinceId,
			taxYear,
			status,
		],
		queryFn: () => {
			return CompanyTD1FormService.getEmployeeDDLByStatusAndByProvince(
				clientId,
				provinceId,
				taxYear,
				status
			);
		},
		refetchOnWindowFocus: false,
	});

const useGetDataEmployeeTD1FormDDL = (
	employeeId: string,
	provinceId: string,
) =>
	useQuery({
		queryKey: [
			TD1_FORM_BY_EMPLOYEEID_QUERY_KEY, employeeId, provinceId
		],
		queryFn: () => {
			return CompanyTD1FormService.getDataEmployeeTD1FormDDL(
				employeeId,
				provinceId
			);
		},
		refetchOnWindowFocus: false,
	});




export const TD1FormSetup: FC<ITD1FormSetup> = ({ testId }) => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const navigateToLogout = useNavigateToLogout();
	const clientName = useSelector((state: IAppState) => state.client.clientName);
	const companyInfo = useSelector((state: IAppState) => state.client);
	const canadaProvince = useSelector((state: IAppState) => state.countryAndProvincesInfo.canadaProvinces)


	const isAdminUser = useSelector(
		(state: IAppState) => state.user.profile.isSuperStatus
	);
	const theme = useTheme();
	const [snackbarData, setSnackbarData] = useState<any>(INITIAL_SNACKBAR_DATA);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const [selectedTD1Employee, setSelectedTD1Employee] =
		useState("create-new");
	const [onResourceSelectionAPIsCalls, setOnResourceSelectionAPIsCalls] =
		useState(false);
	const [openDialog, setDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [TD1FormId, setTD1FromId] = useState<any>("");
	const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
	const [TD1FormRecord, setTD1FormRecord] =
		useState<any>(undefined);
	const [employeeStatus, setEmployeeStatus] = useState<number>(100);
	const [isPendingData, setIsPendingData] = useState<boolean>(false);
	const queryClient = useQueryClient();
	const { id, td1FormName } = useParams();

	const [td1FormSlug, setTd1FormSlug] = useState<string>("");
	useEffect(() => {
		if (td1FormName && td1FormName.length > 0) {

			setTd1FormSlug(td1FormName);
			if (td1FormName !== td1FormSlug) {
				setSelectedEmployee(null)
			}
		}
	}, [td1FormSlug, td1FormName]);
	const [editRecordId, setEditRecordId] = useState(id);
	const [selectedDate, setSelectedDate] = useState<any>(undefined);
	const [effectiveDate, setEffectiveDate] = useState<any>();
	const [provinceId, setProvinceId] = useState<any>();
	const [adminTd1FormData, setAdminTd1FormData] = useState<any>(undefined);



	useEffect(() => {
		if (id) {
			setEditRecordId(id);
		}
	}, [id]);


	const formInitialState = () => {
		// setSelectedEmployee(null) // null all bydefault
		setTD1FormRecord(undefined);
		setAdminTd1FormData(undefined);
		// setSelectedTD1Employee("create-new");
		// formMethods.reset();
		setEditRecordId(undefined);
		let path = `/td1/${td1FormSlug}`;
		navigate(path);
	};

	const formMethods = useForm<any>({
		resolver: yupResolver(EmployeeTD1FormValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const { isPending: isSubmitting, mutate: submitTD1FormFields } =
		useMutation({
			mutationFn: CompanyTD1FormService.createTD1EmployeeForm,
			onSuccess: (
				res: ITD1Form | IServerError | IResponse | any
			) => {

				if (res.status === 200) {
					// setTD1FormRecord(res);
					queryClient.invalidateQueries({
						queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, res.data.id],
					});
					queryClient.invalidateQueries({
						queryKey: [
							TD1_FORM_BY_EMPLOYEEID_QUERY_KEY,
							res.data.employeeId,
							res.data.province.id,
						],
					});
					setEditRecordId(res.data.id);
					scrollPanelFieldsToTop();
					let path = `/td1/${td1FormSlug}/${res.data.id}`;
					navigate(path);
					// setSelectedTD1Employee(res.data.id);

					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else if (res.status === 201) {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					queryClient.invalidateQueries({
						queryKey: [
							SINGLE_RECORD_DATA_QUERY_KEY,
							selectedEmployee,
						],
					});
					queryClient.invalidateQueries({
						queryKey: [
							TD1_FORM_BY_EMPLOYEEID_QUERY_KEY,
							res.data.employeeId,
							res.data.province.id,
						],
					});
					setEditRecordId(res.data.id)
					scrollPanelFieldsToTop();
					let path = `/td1/${td1FormSlug}/${res.data.id}`;
					navigate(path);
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "something went wrong!",
						severity: "error",
					});
				}
			},
			onError: (err: any) => {
				setSnackbarData({
					open: true,
					message: err.message,
					severity: "error",
				});
			},
		});

	const { isPending: isPendingTd1FormById, data: selectedTD1EmployeeDataByIddetails } = useGetDataTD1Detail(editRecordId);


	// const { isPending: isPendingTD1EmployeeDDL, data: employeeDDL } =
	// 	useGetTD1EmployeeDDL(clientId, employeeStatus, taxYear);
	const { isPending: isPendingTD1EmployeeDDL, data: employeeDDL } =
		useGetEmployeeTD1FormDDLByProvinceAndStatus(clientId, provinceId === 'federal' ? "all" : provinceId, taxYear, employeeStatus);




	// const watchCurrentDate = formMethods.watch("effectiveDate");


	const { isPending: isPendingTD1EmployeeDDLDDL, data: td1EmployeeDDL } =
		useGetDataEmployeeTD1FormDDL(selectedEmployee, provinceId);

	// const { data: td1EmployeeFormData } = useGetTD1EmployeeForm(selectedDate, provinceId, selectedEmployee, clientId)


	useEffect(() => {
		if (selectedTD1EmployeeDataByIddetails) {
			setTD1FormRecord(selectedTD1EmployeeDataByIddetails.td1FormDetail);
		}
	}, [selectedTD1EmployeeDataByIddetails]);

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		scrollPanelFieldsToTop();
		let path = `/td1/${td1FormSlug}`;
		navigate(path);
		formInitialState();
		setSelectedEmployee(null)
		formMethods.reset();
	};

	// For Delete Row

	const { isPending: isDeleting, mutate: deleteTD1EmployeeById } =
		useMutation({
			mutationFn: CompanyTD1FormService.deleteTD1FormById,

			onSuccess: (res: any | IServerError | IResponse) => {
				if (typeof res === "string") {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					setTD1FormRecord(undefined);
					queryClient.invalidateQueries({
						queryKey: [
							TD1_FORM_BY_EMPLOYEEID_QUERY_KEY,
							selectedEmployee, provinceId,
						],
					});
					setEditRecordId(undefined);
					scrollPanelFieldsToTop();
				} else {
					setSnackbarData({
						open: true,
						message: res.message,
						severity: "error",
					});
				}
			},
			onError: (err: any) => {
				setSnackbarData({
					open: true,
					message: err.message,
					severity: "error",
				});
			},
		});

	const deleteSalaryTD1Field = () => {
		if (singleDelete) {
			let payload = {
				// TD1FormId,
				// clientId,
				// taxYear,
				id: TD1FormId
			};
			deleteTD1EmployeeById(payload);
			setSingleDelete(false);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (
			selectedTD1EmployeeDataByIddetails &&
			selectedTD1EmployeeDataByIddetails.id
			// && editedRecordSlipCreateNew === false
		) {
			setTD1FromId(selectedTD1EmployeeDataByIddetails.id);
		}
	};
	/**
	 * On Snackbar closed.
	 *
	 * @param {React.SyntheticEvent | Event} event
	 * @param {string} reason
	 */
	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const onEmployeeSelectionChangeHandler = async (
		selectedItem: IEnhancedDropDownItem | any
	): Promise<void> => {

		if (selectedEmployee !== selectedItem.value) {
			setSelectedEmployee(selectedItem.value);
			formInitialState();
			// setTD1FormRecord(undefined)
			// formMethods.reset();
		}
	};

	const onTD1EmployeeSelectionChangeHandler = (
		selectedItem: IEnhancedDropDownItem | any
	) => {
		if (selectedItem.value === "create-new") {
			setSelectedTD1Employee("create-new");
			// formMethods.setValue(`tD1FieldId_${item.indexForValue}`, item.value ?? 0);
			// formMethods.setValue('setClaimCode', false);
			// setTD1FormRecord(undefined);
			setAdminTd1FormData(undefined);
			setEditRecordId(undefined);
			let path = `/td1/${td1FormSlug}`;
			navigate(path);
			(async () => {
				try {
					setIsPendingData(true)
					const effectiveDateResponse = await TD1FormServices.getEffectiveDateOfLatestTD1FormByYearAndProvinceId(
						provinceId,
						taxYear,
						navigateToLogout,
					);
					if (effectiveDateResponse.effectiveDate) {
						(async () => {
							try {
								setIsPendingData(true)
								formMethods.reset();
								// const currentDate = new Date();
								// const formattedDate = formatDate(currentDate)
								const response = await CompanyTD1FormService.getDataByProvinceAndCurrentDate(
									navigateToLogout,
									formatDate(effectiveDateResponse.effectiveDate),
									provinceId,
								);
								// formMethods.setValue("effectiveDate", currentDate)



								if (
									response?.data &&
									response.data.message ===
									"No TD1 Form found for this province"
								) {
									setSnackbarData({
										open: true,
										message: "TD1 Form not exists!",
										severity: "warning",
									});
									setAdminTd1FormData(undefined);
								} else {
									setAdminTd1FormData(response);
								}
							} catch (error) {
								setIsPendingData(false)
								console.error(error);
							} finally {
								setIsPendingData(false)
							}
						})();
					} else {
						setSnackbarData({
							open: true,
							message: "TD1 Form not exists!",
							severity: "warning",
						});
						setAdminTd1FormData(undefined);
						setIsPendingData(false)
					}
				} catch (error) {
					setIsPendingData(false)
				}
			})();

		}
		if (selectedItem.value !== "create-new") {
			formMethods.reset();
			setSelectedTD1Employee(selectedItem.value);
			setEditRecordId(selectedItem.value);
			let path = `/td1/${td1FormSlug}/${selectedItem.value}`;
			navigate(path);
		}
	};

	const onEmployeeTypeSelectionChangeHandler = (event: any) => {
		setEmployeeStatus(event.value);
		setSelectedEmployee(null);
		formInitialState();
		formMethods.setValue('employeeType', event.value)
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};

	// const updatedTD1EmployeeDDL = useMemo(() => {
	// 	if (td1EmployeeDDL && Array.isArray(td1EmployeeDDL)) {
	// 		let employeeDDLList: IDropDownListResponse[] = sortDDLByLabel(td1EmployeeDDL).map(
	// 			(resourceSlip: IDropDownListResponse) => ({
	// 				label: resourceSlip.label,
	// 				value: resourceSlip.value,
	// 			})
	// 		);
	// 		employeeDDLList.unshift({
	// 			label: "Create New",
	// 			value: "create-new",
	// 		});

	// 		if (editRecordId) {
	// 			setSelectedTD1Employee(String(editRecordId));
	// 		} else if (td1EmployeeDDL && td1EmployeeDDL.length > 0) {
	// 			setSelectedTD1Employee(String(td1EmployeeDDL[0].value));
	// 			setEditRecordId(String(td1EmployeeDDL[0].value));
	// 			let path = `/td1/${td1FormSlug}/${td1EmployeeDDL[0].value}`;
	// 			navigate(path);
	// 		}
	// 		// setTD1FormRecord(undefined);
	// 		return employeeDDLList;
	// 	}
	// 	else {
	// 		return [
	// 			{
	// 				label: "Create New",
	// 				value: "create-new",
	// 			},
	// 		];
	// 	}
	// }, [td1EmployeeDDL, TD1FormRecord]);

	const updatedTD1EmployeeDDL = useMemo(() => {
		if (td1EmployeeDDL && Array.isArray(td1EmployeeDDL)) {
			const employeeDDLList: IDropDownListResponse[] =
				sortDDLByLabel(td1EmployeeDDL).map(
					(resourceSlip: IDropDownListResponse) => ({
						label: resourceSlip.label,
						value: resourceSlip.value,
					})
				);

			employeeDDLList.unshift({
				label: "Create New",
				value: "create-new",
			});

			return employeeDDLList;
		}

		return [
			{
				label: "Create New",
				value: "create-new",
			},
		];
	}, [td1EmployeeDDL]);

	useEffect(() => {
		if (!Array.isArray(td1EmployeeDDL)) {
			return;
		}

		// No records -> Create New
		if (td1EmployeeDDL.length === 0) {
			setSelectedTD1Employee("create-new");
			setAdminTd1FormData(undefined);
			setEditRecordId(undefined);
			let path = `/td1/${td1FormSlug}`;
			navigate(path);

			return;
		}

		// If we already have a valid editRecordId,
		// don't automatically replace it.
		if (editRecordId) {
			setSelectedTD1Employee(String(editRecordId));
			return;
		}

		// Get latest record according to label
		const latestRecord = td1EmployeeDDL.reduce(
			(latest: IDropDownListResponse, current: IDropDownListResponse) =>
				new Date(current.label) > new Date(latest.label)
					? current
					: latest
		);

		const latestValue = latestRecord.value;

		setSelectedTD1Employee(String(latestValue));
		setEditRecordId(String(latestValue));

		let path = `/td1/${td1FormSlug}/${latestValue}`;
		navigate(path);


	}, [
		td1EmployeeDDL,
		// editRecordId,
		// td1FormSlug,
		// navigate,
	]);



	useEffect(() => {
		if (td1EmployeeDDL && td1EmployeeDDL.length === 0) {
			setSelectedTD1Employee("create-new");
			(async () => {
				try {
					setIsPendingData(true)
					const effectiveDateResponse = await TD1FormServices.getEffectiveDateOfLatestTD1FormByYearAndProvinceId(
						provinceId,
						taxYear,
						navigateToLogout,
					);
					if (effectiveDateResponse.effectiveDate) {
						(async () => {
							try {
								setIsPendingData(true)
								// const currentDate = new Date();
								// const formattedDate = formatDate(currentDate)
								const response = await CompanyTD1FormService.getDataByProvinceAndCurrentDate(
									navigateToLogout,
									formatDate(effectiveDateResponse.effectiveDate),
									provinceId,
								);
								// formMethods.setValue("effectiveDate", currentDate)



								if (
									response?.data &&
									response.data.message ===
									"No TD1 Form found for this province"
								) {
									setSnackbarData({
										open: true,
										message: "TD1 Form not exists!",
										severity: "warning",
									});
									setAdminTd1FormData(undefined);
								} else {
									setAdminTd1FormData(response);
									response.map((item: any) => item.indexForValue !== 1 && formMethods.setValue(`tD1FieldId_${item.indexForValue}`, null));
								}
							} catch (error) {
								setIsPendingData(false)
								console.error(error);
							} finally {
								setIsPendingData(false)
							}
						})();
					} else {
						setSnackbarData({
							open: true,
							message: "TD1 Form not exists!",
							severity: "warning",
						});
						setAdminTd1FormData(undefined);
						setIsPendingData(false)
					}
				} catch (error) {
					setIsPendingData(false)
				}
			})();
		}
		//  else {
		// 	setAdminTd1FormData(undefined);
		// }
	}, [td1EmployeeDDL]);


	const tableScrollRef = useRef<HTMLDivElement>(null);
	const updatedTD1EmployeeDDLItems = useMemo(() => {

		return (
			employeeDDL &&
			sortDDLByLabel(employeeDDL).map((item: any) => ({
				...item,
				label: (
					<Link
						// to={`/td1/${td1FormSlug}/${item.value}`}
						to={`/td1/${td1FormSlug}`}
						className="no-underline"
						style={{ color: theme.palette.primary.light, cursor: "pointer" }}
						onClick={(e) => {
							if (item.value === selectedEmployee) {
								e.preventDefault(); // same employee: keep current record and URL
								return;
							}
							onEmployeeSelectionChangeHandler(item)
							scrollPanelFieldsToTop();
						}}
					>
						{item.label}
					</Link>
				),
			}))
		);
	}, [employeeDDL, td1FormSlug, selectedEmployee]);

	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const getProvinceShortCode = (td1FormSlug: any, provinces: any) => {
		if (td1FormSlug === "federal") return "Federal";

		const found = provinces.find(
			(p: any) => p.label.toLowerCase().replace(/\s+/g, "_") === td1FormSlug
		);

		return found?.shortCode || "";
	};
	const result = getProvinceShortCode(td1FormSlug, canadaProvince);

	const expandedLabels = ["Compliance", "TD1"];
	const expandedT4SetupItem = "TD1";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (parentLabel === "Compliance" && item.label === expandedT4SetupItem) {
				isExpanded = true;
			}
			const matchingProvince = canadaProvince?.find(
				(province: any) => province.shortCode === item.label
			);
			const itemSlug = matchingProvince
				? matchingProvince.label.toLowerCase().replace(/\s+/g, '_')
				: item.label === "Federal"
					? "federal"
					: null;
			let itemPath = item.path;
			if (parentLabel === "TD1" && itemSlug) {
				itemPath = `/td1/${itemSlug}`;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: itemPath,
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	console.log(canadaProvince)

	const onSubmit = (values: any) => {
		console.log('valuesvaluesvaluesvalues', values)




		const currentProvince = canadaProvince?.find(
			(province: any) => province.label.toLowerCase().replace(/\s+/g, '_') === td1FormSlug
		);


		const provinceTD1Object = {
			id: currentProvince?.value,
			name: currentProvince?.label,
			shortCode: currentProvince?.shortCode
		};


		const td1FormDetailData = adminTd1FormData ? adminTd1FormData : TD1FormRecord ? TD1FormRecord : [];


		let payload = {
			clientId: clientId,
			id: id && selectedTD1EmployeeDataByIddetails.effectiveDate === formatDate(values.effectiveDate) ? id : undefined,
			employeeId: selectedEmployee,
			setClaimCode: id ? values.setClaimCode : undefined,
			province: td1FormSlug === "federal" ? {
				id: "federal",
				name: "Federal",
				shortCode: "FD",
			} : provinceTD1Object,
			effectiveDate: values.effectiveDate !== null && values.effectiveDate !== ""
				? formatDate(values.effectiveDate)
				: undefined,

			// td1FormDetail: td1FormDetailData
			totalClaimAmount:
				id && selectedTD1EmployeeDataByIddetails && values.setClaimCode ? selectedTD1EmployeeDataByIddetails.totalClaimAmount :
					id && selectedTD1EmployeeDataByIddetails && !values.setClaimCode && selectedTD1EmployeeDataByIddetails.totalClaimAmount === 0 ?
						values.tD1FieldId_1 :
						undefined,
			td1FormDetail: td1FormDetailData
				.map((item: any) => {
					let fieldValue = values[`tD1FieldId_${item.indexForValue}`];

					if (
						(item.indexForValue === 1 && !values.setClaimCode && item.value === 0) ||
						(item.indexForValue === 1 && !values.setClaimCode && !item.value)
					) {
						fieldValue = item.templateValue;
					} else if (item.indexForValue === 1) {
						fieldValue = item.value;
					}


					if (fieldValue === 0 || fieldValue === null || fieldValue === undefined) {
						fieldValue = null;
					}

					return {
						heading: item.heading,
						detail: item.detail,
						value: values.setClaimCode ? 0 : fieldValue,
						indexForValue: item.indexForValue

					};
				}),

			// td1FormDetail: adminTd1FormData ? adminTd1FormData.td1FormDetail.map((item: any) => ({
			// 	heading: item.heading,
			// 	detail: item.detail,
			// 	value: values[`tD1FieldId_${item.indexForValue}`] === values["tD1FieldId_1"] ? values.tD1FieldId_1 : values[`tD1FieldId_${item.indexForValue}`],
			// 	indexForValue: item.indexForValue
			// })) : TD1FormRecord ? TD1FormRecord.td1FormDetail.map((item: any) => ({
			// 	heading: item.heading,
			// 	detail: item.detail,
			// 	// value: values[`tD1FieldId_${item.indexForValue}`],
			// 	value: values[`tD1FieldId_${item.indexForValue}`] === values["tD1FieldId_1"] ? values.tD1FieldId_1 : values[`tD1FieldId_${item.indexForValue}`],
			// 	indexForValue: item.indexForValue
			// })) : []
			fedEnhance: td1FormSlug === "federal" ? {
				dateFrom: formatDate(values.dateFrom),
				dateTo: formatDate(values.dateTo),
				// amountForEachDay: values.amountForEachDay,
				amountForEachDay: values.amountForEachDay === "22" ? true : false,
				prescribedZonesEmployee: values.prescribedZonesEmployee,
				additionalTaxToBeDeducted: values.additionalTaxToBeDeducted,
			} : null
			// here
		}
		submitTD1FormFields(payload)
		console.log("payload", payload)

	}

	useEffect(() => {
		if (selectedTD1EmployeeDataByIddetails) {
			Object.keys(selectedTD1EmployeeDataByIddetails).forEach((fieldName) => {
				const fieldValue = selectedTD1EmployeeDataByIddetails[fieldName];
				formMethods.setValue(fieldName, fieldValue ?? undefined);
				if (fieldName === "effectiveDate" && fieldValue !== null) {
					formMethods.setValue(fieldName, new Date(fieldValue) ?? undefined);
				}
				if (fieldName === 'employeeId') {
					setSelectedEmployee(fieldValue)
				}
				// if (fieldName === 'td1FormDetail' && Array.isArray(fieldValue)) {
				// 	fieldValue.forEach((item: any) => {
				// 		if (item.indexForValue && item.indexForValue !== 1) {
				// 			formMethods.setValue(`tD1FieldId_${item.indexForValue}`, item.value);
				// 		}
				// 	});
				// }
				if (fieldName === 'td1FormDetail' && Array.isArray(fieldValue)) {
					fieldValue.forEach((item: any) => {
						if (item.indexForValue) {
							// Only set the actual field value, not the checkbox
							formMethods.setValue(`tD1FieldId_${item.indexForValue}`, item.value);
						}
					});
				}

				if (fieldName === 'fedEnhance') {
					const values = fieldValue ?? {};
					console.log('values.amountForEachDay', values.amountForEachDay)
					if (values.amountForEachDay !== null && values.amountForEachDay !== undefined) {
						formMethods.setValue("amountForEachDay", values.amountForEachDay ? 22 : 11);
					} else {
						formMethods.setValue("amountForEachDay", undefined);
					}

					formMethods.setValue("dateFrom", new Date(`${values.dateFrom}`))
					formMethods.setValue("dateTo", new Date(`${values.dateTo}`))
					formMethods.setValue("prescribedZonesEmployee", values.prescribedZonesEmployee);
					formMethods.setValue("additionalTaxToBeDeducted", values.additionalTaxToBeDeducted);
				}

			});
		}
	}, [selectedTD1EmployeeDataByIddetails, formMethods]);




	useEffect(() => {
		const updatedCanadaProvincesLabels = canadaProvince && canadaProvince.map((obj: any) => ({ ...obj, label: obj.label.toLowerCase().replace(/\s+/g, '_') }));


		const currentLoadedProvince = updatedCanadaProvincesLabels?.find((x: any) => x.label === td1FormSlug)
		if (td1FormSlug === 'federal') {
			setProvinceId(td1FormSlug)
		} else if (currentLoadedProvince) {
			setProvinceId(currentLoadedProvince.value)
		}

	}, [td1FormSlug, canadaProvince]);


	const onPrintClicked = async (): Promise<void> => {
		const maskedStatus: any = sessionStorage.getItem("maskEnabled");
		if (maskedStatus === "true") {
			setSnackbarData({
				open: true,
				message: "Please unmask your data.",
				severity: "error",
			});
		} else {
			// setIsPendingData(true);
			// if (invoicePaymentFor === 10) {
			// 	try {
			// 		const previousYearInvoiceResponse: any =
			// 			await ReportsService.getPreviousYearInvoice(clientId, taxYear - 1);
			// 		try {
			// 			const response: any = await ReportsService.createInvoice(
			// 				clientId,
			// 				taxYear
			// 			);
			// 			setIsPendingData(false);

			// 			if (
			// 				previousYearInvoiceResponse.message ===
			// 					"Slips not found for invoice." &&
			// 				response.message === "Slips not found for invoice."
			// 			) {
			// 				if (
			// 					t4SlipIds &&
			// 					t4SlipIds.generalAuditStatus !== null &&
			// 					t4SlipIds.generalAuditStatus === true &&
			// 					t4SlipIds &&
			// 					t4SlipIds.auditStatus !== null &&
			// 					t4SlipIds.auditStatus === true
			// 				) {
			setIsPendingData(true);
			try {
				let pdfIds = `${id}`;
				const response: any = await PrintingService.createEmployeeTd1PdfPrint(
					// clientId,
					// taxYear,
					selectedEmployee,
					pdfIds,

				);
				if (response.status === 400) {
					setSnackbarData({
						open: true,
						message: response.data.message
							? response.data.message
							: "Something went wrong.",
						// message: "Please Unmask sensitive data for PDF printing.",
						severity: "error",
					});
				} else {
					const filename = `Td1-${td1FormSlug}.pdf`;
					savePdfAsFile(response, filename);
				}
				setIsPendingData(false);
			} catch (err) {

				alert(err);
			}
			setIsPendingData(false);
			// 				} else {
			// 					setIsPendingData(false);
			// 					setSnackbarData({
			// 						open: true,
			// 						message: (
			// 							<>
			// 								Please load Audit in the menu, press Run T4 Audit and
			// 								clear all audit items (Audit Filing and PIER Review) to
			// 								generate documents.{" "}
			// 								<a
			// 									href="https://appv3.taxslips.com/#/audit/filing"
			// 									style={{
			// 										color: "#aa1010",
			// 										textDecoration: "none",
			// 										fontWeight: "bold",
			// 									}}
			// 								>
			// 									Click here to view
			// 								</a>
			// 							</>
			// 						),
			// 						severity: "error",
			// 						autoHide: false,
			// 					});
			// 				}
			// 			} else if (
			// 				response?.id != null &&
			// 				previousYearInvoiceResponse?.id != null
			// 			) {
			// 				
			// 				setInvoiceData([previousYearInvoiceResponse, response]);
			// 				setInvoiceDialogOpen(true);
			// 			} else if (
			// 				previousYearInvoiceResponse?.id != null &&
			// 				response?.message === "Slips not found for invoice."
			// 			) {
			// 				
			// 				setInvoiceData([previousYearInvoiceResponse]);
			// 				setInvoiceDialogOpen(true);
			// 			} else if (
			// 				response?.id != null &&
			// 				previousYearInvoiceResponse?.message ===
			// 					"Slips not found for invoice."
			// 			) {
			// 			
			// 				setInvoiceData([response]);
			// 				setInvoiceDialogOpen(true);
			// 			} else {
			// 				setSnackbarData({
			// 					open: true,
			// 					message: "Something went wrong.",
			// 					severity: "error",
			// 				});
			// 			}
			// 			setIsPendingData(false);
			// 		} catch (err) {
			// 		
			// 			setIsPendingData(false);
			// 		}
			// 	} catch (err) {
			// 	
			// 		setIsPendingData(false);
			// 	}
			// } else {
			// 	try {
			// 		const response: any = await ReportsService.createInvoice(
			// 			clientId,
			// 			taxYear
			// 		);
			// 		setIsPendingData(false);
			// 
			// 		if (response.message === "Slips not found for invoice.") {
			// 			if (
			// 				t4SlipIds &&
			// 				t4SlipIds.generalAuditStatus !== null &&
			// 				t4SlipIds.generalAuditStatus === true &&
			// 				t4SlipIds &&
			// 				t4SlipIds.auditStatus !== null &&
			// 				t4SlipIds.auditStatus === true
			// 			) {
			// 				setIsPendingData(true);
			// 				try {
			// 					const jsonString = formMethods.getValues("provinceOfEmployment")
			// 						? formMethods.getValues("provinceOfEmployment")
			// 						: ('{id:9, name: "Ontario",shortCode:"ON"}' as string);
			// 					const ProvinceOfEmploymentData =
			// 						parseJSONLikeString(jsonString);
			// 					let pdfIds = `t4_slip_ids=${id}`;
			// 					const response: any =
			// 						await PrintingService.createBulkT4PdfPrint(
			// 							clientId,
			// 							printType,
			// 							pdfIds,
			// 							taxYear
			// 						);
			// 					if (response.status === 400) {
			// 						setSnackbarData({
			// 							open: true,
			// 							message: response.data.message
			// 								? response.data.message
			// 								: "Something went wrong.",
			// 							severity: "error",
			// 						});
			// 					} else {
			// 						const employeeFirstName = formMethods.getValues("firstName");
			// 						const employeeLastName = formMethods.getValues("lastName");
			// 						const filename = `T4slip-${employeeFirstName} ${employeeLastName}-${printType}.pdf`;
			// 						savePdfAsFile(response, filename);
			// 					
			// 					}
			// 					setIsPendingData(false);
			// 				} catch (err) {
			// 					
			// 					alert(err);
			// 				}
			// 				setIsPendingData(false);
			// 			} else {
			// 				setIsPendingData(false);
			// 				setSnackbarData({
			// 					open: true,
			// 					message: (
			// 						<>
			// 							Please load Audit in the menu, press Run T4 Audit and clear
			// 							all audit items (Audit Filing and PIER Review) to generate
			// 							documents.{" "}
			// 							<a
			// 								href="https://appv3.taxslips.com/#/audit/filing"
			// 								style={{
			// 									color: "#aa1010",
			// 									textDecoration: "none",
			// 									fontWeight: "bold",
			// 								}}
			// 							>
			// 								Click here to view
			// 							</a>
			// 						</>
			// 					),
			// 					severity: "error",
			// 					autoHide: false,
			// 				});
			// 			}
			// 		} else if (response && response.id !== null) {
			// 			setInvoiceData([response]);
			// 			setInvoiceDialogOpen(true);
			// 		} else {
			// 			setSnackbarData({
			// 				open: true,
			// 				message: "Something went wrong.",
			// 				severity: "error",
			// 			});
			// 		}
			// 		setIsPendingData(false);
			// 	} catch (err) {
			// 	
			// 		setIsPendingData(false);
			// 	}
			// }
		}
	};

	const onResetDeductionPanelClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});

		// formMethods.reset({
		// 	...formMethods.getValues(),
		// 	startDate: null,
		// 	endDate: null,
		// 	amountCalculate: null,
		// });

		formMethods.setValue("dateFrom", null);
		formMethods.setValue("dateTo", null);
		formMethods.setValue("amountForEachDay", undefined);
		formMethods.setValue("prescribedZonesEmployee", false);
		formMethods.setValue("additionalTaxToBeDeducted", null);

	};

	const onRefreshDeductionPanelClicked = () => {
		alert("coming...")
	};

	return (
		<>
			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
				<BackdropCircularProgress
					open={
						isSubmitting ||
						isDeleting ||
						isPendingData ||
						isPendingTD1EmployeeDDL ||
						isPendingTD1EmployeeDDLDDL ||
						isPendingTd1FormById
					}
					testId={testId}
				/>
				<DeleteConfirmationDialog
					open={openDialog}
					deleteActionHandler={deleteSalaryTD1Field}
					cancelActionHandler={onCloseHandler}
					testId="T4-confirmation-dialog"
				/>
				<FormProvider {...formMethods}>
					<form onSubmit={formMethods.handleSubmit(onSubmit)}>
						<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>

							<Grid
								size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}
								sx={{
									borderRadius: "3px",
								}}
							>
								{selectedEmployee !== null && (adminTd1FormData || (id && TD1FormRecord)) ? (
									<SingleColumnLayout
										children={
											<>
												{td1FormSlug === "quebec" ? (<StandardPanel
													title={""}
													borderHide={true}
													// borderBottom={true}
													children={
														<QuebecFormDataConstant

															quebecFormData={id && TD1FormRecord ? TD1FormRecord : adminTd1FormData ? adminTd1FormData : []}
															existingRecordData={adminTd1FormData}
															employeeTD1FromId={id && id}
															selectedTD1Employee={selectedTD1Employee}
															selectedTD1EmployeeDataByIddetails={id && selectedTD1EmployeeDataByIddetails}
															formMethods={formMethods}
															id={id}

														/>
													}
													testId={"undefined"}>

												</StandardPanel>) : (<StandardPanel
													title=""
													testId={`${testId}-slip-data`}
													borderHide={true}
													borderBottom={true}
													children={

														<TD1FormDataConstant
															federalFormData={id && TD1FormRecord ? TD1FormRecord : adminTd1FormData ? adminTd1FormData : []}
															existingRecordData={adminTd1FormData}
															// onDateSelectionChangeHandler={onDateSelectionChangeHandler}
															employeeTD1FromId={id && id}
															selectedTD1Employee={selectedTD1Employee}
															selectedTD1EmployeeDataByIddetails={id && selectedTD1EmployeeDataByIddetails}
															formMethods={formMethods}
															id={id}
															onResetDeductionPanelClicked={onResetDeductionPanelClicked}
															onRefreshDeductionPanelClicked={onRefreshDeductionPanelClicked}
															td1FormSlug={td1FormSlug}
															canadaProvince={canadaProvince}
														/>
													}
												>
												</StandardPanel>)}
											</>
										}
										toolbar={
											<TD1FormToolbar
												onResetClicked={onResetClicked}
												onDeleteClicked={onDeleteClicked}
												selectedTD1EmployeeDataByIddetails={selectedTD1EmployeeDataByIddetails}
												onPrintClicked={onPrintClicked}
												isSubmitting={false}
												formMethods={formMethods}
												id={id}
												pageTitle={`${td1FormSlug === 'federal' ? 'Federal' : canadaProvince?.find((provinceLabel: any) => provinceLabel.label.toLowerCase().replace(/\s+/g, '_') === td1FormSlug)?.label}`} />
										}
										showFullDiaplay={true}
										testId={"TD1-federal-form-setup"} />
								) : (
									<Grid size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
										<Typography
											sx={{
												fontSize: ".875rem", fontWeight: "bold",

											}}
										>
											<h2>{`${td1FormSlug === 'federal' ? 'Federal' : canadaProvince?.find((provinceLabel: any) => provinceLabel.label.toLowerCase().replace(/\s+/g, '_') === td1FormSlug)?.label}`}</h2>
										</Typography>
									</Grid>
								)}

							</Grid>
							<Grid size={{ xs: 0, sm: 0, md: 1, lg: 1, xl: 1 }}></Grid>
							<Grid
								size={{ xs: 12, sm: 12, md: 3, lg: 3, xl: 3 }}
								sx={{ marginTop: "16px" }}
							>
								<ResourceDDLDataTableForSlips
									rows={
										employeeDDL && updatedTD1EmployeeDDLItems
											? updatedTD1EmployeeDDLItems
											: []
									}
									headerField={
										<EnhancedDropDownRHF
											selectName="employeeType"
											testId={`employee-type`}
											items={[
												{
													label: "List All Employees",
													value: "all",
												},
												{
													label: "List Active Employees Only",
													value: 100,
												},
												{
													label: "List Inactive Employees Only",
													value: 200,
												},
												{
													label: "List Terminated Employees Only",
													value: 300,
												},
												{
													label: "List Died Employees Only",
													value: 400,
												},
											]}
											selectedItem={employeeStatus}
											onSelectionChangeHandler={
												onEmployeeTypeSelectionChangeHandler
											}
										/>
									}
									selectedEmployee={selectedEmployee}
									selectedResourceSlipList={updatedTD1EmployeeDDL ?? []}
									selectedResourceSlip={selectedTD1Employee}
									onResourceSlipSelectionChangeHandler={
										onTD1EmployeeSelectionChangeHandler
									}
									tableScrollHeight={"60vh"}
									// selectedId={selectedTD1EmployeeDataByIddetails && id && selectedTD1EmployeeDataByIddetails?.employeeId}
									selectedId={selectedEmployee}
									tableContainerRef={tableScrollRef}
								/>
								<></>
							</Grid>
							<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>
						</Grid>
					</form>
				</FormProvider>
				<EnhancedSnackbar
					message={snackbarData.message}
					onCloseHandler={onSnackbarClosed}
					severity={snackbarData.severity}
					testId={`${testId}-snackbar`}
					open={snackbarData.open}
					autoHide={snackbarData.autoHide}
				/>
			</Grid>
		</>
	);
};
